import { ApplicationResizer } from '@/application/viewport/ApplicationResizer'
import { Model } from '@/model/Model'
import { Application, Container, Renderer } from 'pixi.js'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import * as PIXI from 'pixi.js'

export interface ISymbolsAnimationConfig {
    animation: Record<string, string>
    landingAnimations: Record<string, string>
    idleAnimations: Record<string, string>
}

export interface IReelsTimingsConfig {
    FIRST_SPEED?: number
    START_BOUNCE_EASING: string
    START_BOUNCING_SHIFT: number
    START_BOUNCING_BACK_TIME: number
    REGULAR_SPEED: number
    STOPPING_SPEED: number
    LAST_SPEED?: number
    FIRST_EASING: string
    LAST_EASING: string
    BOUNCE_EASING: string
    BOUNCING_SHIFT: number
    BOUNCING_BACK_TIME: number
    DELAY_BETWEEN_REELS_ON_START: number
    DELAY_BETWEEN_REELS_ON_STOPPING: number
    DELAY_PER_REEL_SYMBOL: number
    DELAY_BEFORE_STOPPING: number
    DELAY_BETWEEN_CASCADE_REELS: number
}

export interface IAnticipationConfig {
    ANTICIPATION_REEL_TIME: number
    NON_WINNINGS_SYMBOLS_STOPPING_SPEED: number
    DELAY_PER_NON_WINNINGS_REEL_SYMBOL: number
    NON_WINNINGS_BOUNCING_SHIFT: number
    NON_WINNINGS_BOUNCING_BACK_TIME: number
    DELAY_BETWEEN_STAGE1_AND_STAGE2: number
    WINNINGS_SYMBOLS_STOPPING_SPEED: number
    DELAY_PER_WINNINGS_REEL_SYMBOL: number
    WINNINGS_BOUNCING_SHIFT: number
    WINNINGS_BOUNCING_BACK_TIME: number
}

export interface IReelsConfig {
    TOTAL_REELS: number
    REELS_GAP: number
    SYMBOLS_GAP: number
    VISIBLE_SYMBOLS_PER_REEL: number
    EXTRA_SYMBOLS_TOP: number
    EXTRA_SYMBOLS_BOTTOM: number
    LAST_EASING: string
    BLUR_FILTER: number
    BASE_REELS_SET: number[]
    SCATTER_SUSPENSE_TIME: number
    FEATURE_SUSPENSE_TIME: number
    FREE_SPINS_TRIGGER_ELIGIBLE_REELS: number[]
    DELAY_BETWEEN_REELS?: number
    DELAY_PER_MULTIPLIER_SYMBOLS: number
    DELAY_BEFORE_TRAIL_ANIMATION: number
    NORMAL: IReelsTimingsConfig
    QUICK: IReelsTimingsConfig
}

export interface ISymbolsConfig {
    TOTAL: number
    WIDTH: number
    HEIGHT: number
    SHADOW_OPACITY: number
    ids: number[]
    indexes: Record<string, any>
    landingIndexes: number[]
    multiplierSettings: Record<string, any>
}

export interface IDisplayConfigLayout {
    WORLD_WIDTH: number
    WORLD_HEIGHT: number
}

export interface IDisplayConfig {
    sizes: Record<string, IDisplayConfigLayout>
}

export interface IWinningBigWinConfig {
    name: string
    from: number
    to: number
    coinAmount: number
    time: number
    fontScale: number
    fontScaleDuration: number
    fontScaleEasing: string
    easing: string
}

export interface IWinningsConfig {
    INITIAL_FONT_SIZE: number
    FINISH_FONT_SIZE: number
    WIN_ANIMATION_TIME: number
    REGULAR_EASING: string
    REGULAR_COUNTING_DURATION: number
    DELAY_BEFORE_CLOSE_BIGWIN: number
    REPLACE_FEATURE_SPEED: number
    MIN_FOR_COUNTING: number
    /** Minor units. Wins below this never play a commendation, whatever their bet multiplier. */
    MIN_BIG_WIN_AMOUNT: number
    BIG_WIN: IWinningBigWinConfig[]
    DELAY_BETWEEN_LINES_WINNINGS: number
    MAX_WIN: number
}

export interface IPayoutsConfig {
    [index: string]: number[]
}

export interface IMobileSwipeSpinConfig {
    enabled: boolean
    minDistance?: number
    maxHorizontalDrift?: number
    preventDefault?: boolean
    requireConfirmation?: boolean
    popup?: {
        title?: string
        text?: string
        enableLabel?: string
        disableLabel?: string
    }
}

export interface IInsufficientBalancePopupConfig {
    message?: string
    deposit?: {
        buttonText?: string
    }
    continue?: {
        buttonText?: string
    }
}

export interface IPromoPopupConfig {
    accept?: {
        buttonText?: string
    }
    decline?: {
        buttonText?: string
    }
    continue?: {
        buttonText?: string
    }
}

export interface IDefaultConfig {
    sound: boolean
    music: boolean
    volume: number
    skipIntro: boolean
    spacebarSpin: boolean
    turboSpin: boolean
    showFastPlayOption?: boolean
    mobileSwipeSpin?: boolean | IMobileSwipeSpinConfig
    insufficientBalancePopup?: IInsufficientBalancePopupConfig
    promoPopup?: IPromoPopupConfig
}

export enum Orientation {
    PORTRAIT = 'portrait',
    LANDSCAPE = 'landscape',
}

export interface PopupDialog {
    label: string
    callback: (close?: () => void) => void
}

export interface PopupTextStyle {
    fill?: string[]
    stroke?: string
    strokeThickness?: number
    fontFamily?: string
}

export interface PopupStyle {
    title?: PopupTextStyle
    text?: PopupTextStyle
    button?: PopupTextStyle
}

export interface PopupContent {
    variant?: string
    title: string
    text: string
    style?: PopupStyle
    buttons: PopupDialog[]
}

export interface IFacade {
    gameVersion: string
    gameId: string
    isDebug: boolean
    isQuickSpin: boolean
    isTurboSpin: boolean
    isAuto: boolean
    requestOngoing: boolean

    displayConfig: IDisplayConfig
    symbolAnimationsConfig: ISymbolsAnimationConfig
    reelsConfig: IReelsConfig
    anticipationConfig: IAnticipationConfig
    symbolsConfig: ISymbolsConfig
    winningsConfig: IWinningsConfig
    defaultConfig: IDefaultConfig

    renderer: Renderer
    resizer: ApplicationResizer
    orientation: Orientation
    app: Application
    container: Container
    stage: Container

    urlParams: IURLParameters
    payouts: IPayoutsConfig
    model: Model
    status: number
    currentState: string
    reelsStates: string[]
}

export interface SymbolContainerData {
    index: number
    multiplier?: number
    coinValue?: number
    coinType?: string
    symbolKey?: string
}

export interface BigWinCounterData {
    timerId: number
    duration: number
    amount: number
}

export type CascadeSpinData = {
    slotGrid: SymbolData[][]
    winningCombination: WinCombination[]
    winPositions: number[][]
    suspenseSpin: boolean
    cascadeReels: number[]
}

export type FreeSpinsActivateData = {
    baseWinCurrency: number
    winCurrency: number
    symbolsPositions: number[][]
}

export type SymbolData = {
    id: number
    multiplier?: number
    coinValue?: number
    coinType?: string
    symbolKey?: string
    spinCoinValue?: number
}

export type WinCombination = {
    pay_multiplier: number
    symbol_count: number
    symbol_id: string
    payline_number?: number
    payline_path?: number[][]
    symbols_positions: number[][]
    win_coin: number
    win_currency: number
}

export type BonusData = {
    bonus_amount: number
    bonus_win_currency: number
    freebet_count: number
    freebet_given: number
    freebets_position: any[]
    jackpot_won: any[]
    multipliers: number[]
    scatter_count: number
    scatters_position: any[]
    num_coin_symbols?: number
}

export interface IHoldAndWinExtraData {
    coin_index: number[]
    coin_values: number[]
    coin_types: string[]
    num_coin_symbols: number
    ladder_count?: number
    ladder_unlock_milestones_count?: number[]
}

export type ShowRegularWinningsData = {
    combinations: WinCombination[]
    win: number
    bet: number
    winPositions: number[][]
}

export type ShowBigWinData = {
    win: number
    bet: number
    bigWinLevel: number
    /** Free Spins: show only the highest commendation, skip intermediate levels. */
    highestOnly?: boolean
    /** The win meter was already synchronized to this summarized total. */
    skipWinCounter?: boolean
}

export interface IAnimationWrapper extends PIXI.Container {
    animation: SpineAnimation
}

export type Payout = {
    length: number
    pay_multiplier: number
    symbols: SymbolData[]
}

export type FreebetLogicData = {
    level: number
    lines_played: number
}

export type FreebetData = {
    amount_left: number
    coin_amount: number
    coin_value: number
    logic: FreebetLogicData
    total_amount_given: number
    total_coin_win: number
    total_win: number
    triggered_win_amount: number
    triggered_win_amount_currency: number
    triggered_win_bonus_amount: number
    triggered_win_bonus_amount_currency: number
}

export type FreeRoundData = {
    amount_left: number
    coin_amount: number
    coin_value: number
    is_active: boolean
    total_amount_given: number
    total_coin_win: number
    total_win: number
}

export interface IBuyBonusRequest {
    sessionToken: string
    amount: number
    type: string
    coinValue: number
    buy_freebet_id: number
}

export type RealityCheckData = {
    startSessionTime: number
    totalSessionBet: number
    totalSessionWin: number
}

export type ReelData = {
    reelIndex: number
    reelGrid: number[]
    originalGrid: SymbolData[]
    activeReelStrip: number[]
}

export type NewSymbolData = {
    symbolData: SymbolContainerData
    allowUnmasked: boolean
}

export type CombinationsData = {
    isWild: boolean
    isHP: boolean
    isLP: boolean
}

export interface IInitPlayerData {
    first_name: string
    user_name: string
    account_id: number
    balance: number
    cash: string
    currency: string
    bonus_amount: string
}

export interface IInitGameData {
    game_id: string
    game_name: string
    num_rows: string
    num_columns: string
    min_coins?: string
    max_coins?: string
    default_coins?: string
    num_coins: string
    default_coin_value: number
    coin_values: string
    fixed_coins: string
    paylines_type?: string
    num_paylines: string
    num_paylines_fs?: string
    rtp: string
    gamble_max_bet: number
    misc?: IInitGameMisc
}

export interface IInitGameMisc {
    /** Server entitlement: 0 means the session may not buy the bonus, so the button must be hidden. */
    buy_bonus?: number | string
}

export interface IInitPreviousRound {
    round_id: string
    matrix: string
    win_amount: number
    coin_value: number
    num_coins?: string
    num_paylines?: string
    amount_type?: string
    sticky_win_details?: string
    other_feature_win_details?: string
    bonus_details?: string
    progress_bar?: any[]
}

export interface IInitNextRound {
    type: string
    num_spins: string
    spins_left: string
    multiplier?: string
    parent_type?: string
    coin_value: string
    total_fs_win_amount: number
}

export interface IInitResponse {
    player: IInitPlayerData
    game: IInitGameData
    current_round: any[]
    next_round: IInitNextRound | any[]
    promo_details: any[]
    previous_round: IInitPreviousRound | null
}

export interface ISpinResponse {
    balance: number
    base_win_currency: number
    bonus: BonusData
    currency?: string
    amount_type?: string
    promo_details?: any
    freebet: FreebetData
    freeround: FreeRoundData
    is_jackpot_win?: boolean
    //## Server capped the round win — current_round.misc_prizes.capamount_reached.
    cap_amount_reached?: boolean
    jackpot_amount?: number
    message?: string
    session_token: string
    slot_grid: SymbolData[][][]
    spin_type?: string
    next_round_type?: string
    respins_left?: number
    respins_total?: number
    hold_and_win_extra?: IHoldAndWinExtraData
    status: number
    win_coin?: number
    win_currency: number
    winning_combinations: WinCombination[][]
}

export interface ISpinRequest {
    sessionToken: string
    amount: number
    coinValue: number
    requestType?: number
    pickPosition?: number
    bonusGameId?: number
}

export interface IGambleRequest {
    position: number
    betAmount: number
}

export interface IURLParameters {
    gameCode: string
    game_code?: string
    platform: string
    casinoToken: string
    currency: string
    language: string
    playForFun: boolean
    playerUid: string
    realityCheckTime: string
    extParams: any
    ext_params: any
    customer: string
    maxBet: boolean
    autoSpin: string
    quickSpin: string
    forceCoinAmount: boolean
    lobby_url?: string
    lobbyUrl?: string
    home_url?: string
    homeUrl?: string
    deposit_url?: string
    depositUrl?: string
    amount_type?: string
    session_id?: string
    site_code?: string
    siteCode?: string
    network_code?: string
    networkCode?: string
    dir?: string
    hide_promo_btn?: string
    hidePromoBtn?: string
}

export interface IInitReelsData {
    originalGrid: SymbolData[][]
    reels: number[][]
}

export interface IStoppingReelsData {
    originalGrid: any[][]
    anticipationIndices: number[]
    isScatterAnticipation?: boolean
    reels: number[][]
    isHoldAndWinRespin?: boolean
    holdAndWinCoinIndex?: number[]
    holdAndWinCoinValues?: number[]
    holdAndWinCoinTypes?: string[]
    holdAndWinSpinsLeft?: number
    holdAndWinSpinsTotal?: number
}
